import {
  ToastAction,
  ToastBody,
  ToastClose,
  ToastContent,
  ToastDescription,
  ToastHeader,
  ToastIcon,
  ToastRoot,
  ToastTitle,
  ToastViewport,
  useToastManager,
} from '@curri/components'
import { useEffect, useRef } from 'react'

import { useAuth } from '../auth/AuthProvider.tsx'

// Sign-in failures, raised as a toast.
//
// A banner had to live somewhere in the layout, and there was no good somewhere:
// above the sheet it pushed the whole shell down, inside it competed with the
// page. A toast is transient chrome, which is what "sign-in did not work" is —
// and it shows wherever the redirect lands, which is the property that matters,
// since the round-trip can finish on any route.
//
// `timeout: 0` — it does not auto-dismiss. A failed sign-in is not incidental,
// and the visitor may have looked away during the redirect.
export const SessionToast = () => {
  const { state, signIn } = useAuth()
  const manager = useToastManager()
  // The message identifies the failure; without this, every render of an error
  // state would stack another copy of the same toast.
  const raised = useRef<string | null>(null)

  useEffect(() => {
    if (state.status !== 'error') {
      raised.current = null
      return
    }
    if (raised.current === state.message) return

    raised.current = state.message
    manager.add({
      actionProps: { children: 'Try again', onClick: signIn },
      description: state.message,
      timeout: 0,
      title: 'Sign-in failed',
      type: 'warning',
    })
  }, [state, manager, signIn])

  return (
    <ToastViewport>
      {manager.toasts.map(toast => (
        <ToastRoot key={toast.id} toast={toast}>
          <ToastContent>
            <ToastIcon type={toast.type} />
            <ToastBody>
              <ToastHeader>
                <ToastTitle />
                <ToastDescription />
              </ToastHeader>
              {toast.actionProps && <ToastAction {...toast.actionProps} />}
            </ToastBody>
            <ToastClose />
          </ToastContent>
        </ToastRoot>
      ))}
    </ToastViewport>
  )
}
